import React, { useMemo } from 'react';
import { PaymentMethod } from '@stripe/stripe-js';

import { showSnackbar } from 'helpers/snackbar';

import { Icon, Layout, Modal, Text } from 'ds';
import { deletePaymentMethod } from 'ds/StripePaymentMethodModal/requests';
import { actions } from 'store/Tenancy';
import { selectBuildingUnitTenanciesWithRent, selectServicesPaymentMethod } from 'store/Tenancy/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';

import TenantPaymentMethodCardOption from './TenantPaymentMethodCardOption';
import { updateTenancyPaymentMethodIds } from '../requests';

interface MobileTenantAdminPaymentCardMenuProps {
  isVisible: boolean;
  onClose: () => void;
  isServicesPaymentMethod: boolean;
  isRentPaymentMethod: boolean;
  paymentMethod: PaymentMethod | null;
}

const MobileTenantAdminPaymentCardMenuModal: React.FC<MobileTenantAdminPaymentCardMenuProps> = ({
  isVisible,
  onClose,
  paymentMethod,
  isServicesPaymentMethod,
  isRentPaymentMethod
}) => {
  const dispatch = useAppDispatch();
  const tenancyId = useAppSelector(selectTenancyId);
  const buildingUnitTenanciesWithRent = useAppSelector(selectBuildingUnitTenanciesWithRent);
  const rentAvailable = buildingUnitTenanciesWithRent && buildingUnitTenanciesWithRent?.length > 0;
  const servicesPaymentMethod = useAppSelector(selectServicesPaymentMethod);

  const handleRemovePaymentMethod = async () => {
    if (!paymentMethod?.id || tenancyId == null) return;
    try {
      const paymentMethods = await deletePaymentMethod({ id: paymentMethod?.id, tenancyId });
      showSnackbar({
        message: 'Payment method removed'
      });
      dispatch(actions.setPaymentMethods(paymentMethods.data));
      onClose();
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to remove payment method',
        negative: true
      });
    }
  };

  const handleAssignToServicesAndSetAsDefault = async () => {
    if (!tenancyId || !paymentMethod?.id) return;
    try {
      const response = await updateTenancyPaymentMethodIds({
        paymentMethodIdServices: paymentMethod?.id,
        tenancyId
      });

      if (response.status === 200) {
        showSnackbar({
          message: rentAvailable ? 'Payment method assigned to services' : 'Payment method set as default'
        });
        dispatch(actions.setPaymentMethods(response.data));

        const servicesPaymentMethod = response.data.find(
          (paymentMethod: PaymentMethod) => paymentMethod.metadata.services_payment_method
        );

        dispatch(actions.setServicesPaymentMethod(servicesPaymentMethod));
        onClose();
      }
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to update payment method',
        negative: true
      });
    }
  };

  const handleAssignToRent = async () => {
    if (!tenancyId || !paymentMethod?.id) return;
    try {
      const response = await updateTenancyPaymentMethodIds({
        paymentMethodIdRent: paymentMethod?.id,
        tenancyId
      });

      if (response.status === 200) {
        showSnackbar({
          message: 'Payment method assigned to rent'
        });
        dispatch(actions.setPaymentMethods(response.data));

        const rentPaymentMethod = response.data.find(
          (paymentMethod: PaymentMethod) => paymentMethod.metadata.rent_payment_method
        );

        dispatch(actions.setRentPaymentMethod(rentPaymentMethod));
        onClose();
      }
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to update payment method',
        negative: true
      });
    }
  };

  const gutterTop = useMemo(() => {
    if (!rentAvailable) return 400;
    if (
      !isServicesPaymentMethod &&
      !paymentMethod?.card &&
      !(isServicesPaymentMethod && servicesPaymentMethod?.card) &&
      !isRentPaymentMethod
    )
      return 300;
    if (!isServicesPaymentMethod) return 400;
    if (!paymentMethod?.card && !(isServicesPaymentMethod && servicesPaymentMethod?.card) && !isRentPaymentMethod)
      return 450;
    if (!isServicesPaymentMethod && !isRentPaymentMethod) return 300;
  }, [rentAvailable, isServicesPaymentMethod, paymentMethod?.card, servicesPaymentMethod?.card, isRentPaymentMethod]);

  return (
    <Modal
      bottomSheetGutterTop={gutterTop}
      isBottomSheet
      bottomSheetIsRounded
      isVisible={isVisible}
      onClose={onClose}
      header={
        <Layout direction="row" gap={4}>
          <Text size="headline-xs">
            {paymentMethod?.card?.brand
              ? paymentMethod.card.brand.charAt(0).toUpperCase() + paymentMethod.card.brand.slice(1)
              : paymentMethod?.us_bank_account?.bank_name}
          </Text>
          <Text size="headline-xs">
            {paymentMethod?.us_bank_account
              ? `•••• ${paymentMethod.us_bank_account?.last4}`
              : `•••• ${paymentMethod?.card?.last4}`}
          </Text>
        </Layout>
      }
      headerAlign="left"
    >
      <Layout width="100%" color="white" paddingX={24} direction="column" gap={24} height="100%" paddingTop={8}>
        {rentAvailable ? (
          <>
            {!isServicesPaymentMethod && (
              <TenantPaymentMethodCardOption
                icon="featherDuster"
                title="Assign to services"
                onClick={handleAssignToServicesAndSetAsDefault}
              />
            )}
            {!paymentMethod?.card &&
              !(isServicesPaymentMethod && servicesPaymentMethod?.card) &&
              !isRentPaymentMethod && (
                <TenantPaymentMethodCardOption icon="buildingNew" title="Assign to rent" onClick={handleAssignToRent} />
              )}
          </>
        ) : (
          <TenantPaymentMethodCardOption
            icon="starOutline"
            title="Set as default"
            onClick={handleAssignToServicesAndSetAsDefault}
          />
        )}
        {!isServicesPaymentMethod && !isRentPaymentMethod && (
          <TenantPaymentMethodCardOption
            customIcon={
              <Layout
                borderRadius="circular"
                __style={{ borderWidth: 1.5 }}
                borderStyle="solid"
                display="inline-flex"
                borderColor="red-900"
                width={24}
                height={24}
                align="center"
                justify="center"
              >
                <Icon name="minus" size="sm" color="red-900" stroke={2} />
              </Layout>
            }
            textColor="red-900"
            title="Remove payment method"
            onClick={handleRemovePaymentMethod}
          />
        )}
      </Layout>
    </Modal>
  );
};

export default MobileTenantAdminPaymentCardMenuModal;
